/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { createDefaultDataStore } from "../data/index.js";
import { asyncGen, runGraph } from "../index.js";
import { createLoader } from "../loader/index.js";
import { timestamp } from "../timestamp.js";
import { Diagnostics } from "./diagnostics.js";
import { extractError } from "./error.js";
import { baseURL } from "./url.js";
const fromProbe = (probe) => {
    const data = structuredClone(probe.data);
    return {
        type: probe.type,
        data,
        result: probe.type === "nodestart" ? probe.result : undefined,
        reply: async () => {
            // Do nothing
        },
    };
};
const fromRunnerResult = (result) => {
    const { type, node, timestamp, invocationId } = result;
    const bubbled = invocationId == -1;
    if (type === "input") {
        const { inputArguments, path } = result;
        return {
            type,
            data: { node, inputArguments, path, bubbled, timestamp },
            reply: async (value) => {
                result.inputs = value.inputs;
            },
        };
    }
    else if (type === "output") {
        const { outputs, path } = result;
        return {
            type,
            data: { node, outputs, path, timestamp, bubbled },
            reply: async () => {
                // Do nothing
            },
        };
    }
    throw new Error(`Unknown result type "${type}".`);
};
const endResult = (last) => {
    return {
        type: "end",
        data: { timestamp: timestamp(), last },
        reply: async () => {
            // Do nothing
        },
    };
};
const errorResult = (error) => {
    return {
        type: "error",
        data: { error, timestamp: timestamp() },
        reply: async () => {
            // Do nothing
        },
    };
};
const maybeSaveProbe = (result, last) => {
    const { type, data } = result;
    if (type === "skip") {
        return {
            node: data.node,
            missing: data.missingInputs,
        };
    }
    return last;
};
const maybeSaveResult = (result, last) => {
    const { type, node } = result;
    if (type === "output" || type === "input") {
        return {
            node,
            missing: [],
        };
    }
    return last;
};
const load = async (config) => {
    const base = baseURL(config);
    const loader = config.loader || createLoader();
    const loadResult = await loader.load(config.url, { base });
    if (!loadResult.success) {
        throw new Error(`Unable to load graph from "${config.url}": ${loadResult.error}`);
    }
    return loadResult;
};
export async function* runLocally(config, kits) {
    yield* asyncGen(async (next) => {
        const graphToRun = config.runner
            ? { graph: config.runner }
            : await load(config);
        const loader = config.loader || createLoader();
        const store = config.store || createDefaultDataStore();
        const fileSystem = config.fileSystem;
        const { base, signal, inputs, state, start, stopAfter, graphStore } = config;
        try {
            let last;
            const probe = config.diagnostics
                ? new Diagnostics(async (message) => {
                    last = maybeSaveProbe(message, last);
                    await next(fromProbe(message));
                })
                : undefined;
            for await (const data of runGraph(graphToRun, {
                probe,
                kits,
                loader,
                store,
                fileSystem,
                base,
                signal,
                inputs,
                state,
                start,
                stopAfter,
                graphStore,
            })) {
                last = maybeSaveResult(data, last);
                await next(fromRunnerResult(data));
            }
            await next(endResult(last));
        }
        catch (e) {
            const error = extractError(e);
            console.error("Local Run error:", error);
            await next(errorResult(error));
        }
    });
}
//# sourceMappingURL=local.js.map